import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  signal,
  Signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { NgClass, NgOptimizedImage } from '@angular/common';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

@Component({
  selector: 'lib-or-avatar',
  templateUrl: './or-avatar.component.html',
  styleUrls: ['./or-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [IonicModule, NgClass, NgOptimizedImage]
})
export class OrAvatarComponent {
  readonly avatarUrl: InputSignal<string | null | undefined> = input<string | null | undefined>();
  readonly initials: InputSignal<string | undefined> = input<string | undefined>('');
  readonly base64: InputSignal<string | undefined> = input<string | undefined>();
  readonly showWhiteBorder: InputSignal<boolean> = input<boolean>(false);
  readonly rightMargin: InputSignal<boolean> = input<boolean>(true);
  readonly avatarSize: InputSignal<AvatarSize> = input.required<AvatarSize>();
  readonly priority: InputSignal<boolean> = input<boolean>(false);

  readonly imageFallback: WritableSignal<boolean> = signal<boolean>(false);

  readonly computedInitials: Signal<string> = computed<string>(() => {
    const initialsValue: string | undefined = this.initials();
    const nameParts: string[] = initialsValue ? initialsValue.split(' ') : [];
    const a: string = nameParts[0]?.charAt(0).toUpperCase() ?? '';
    const b: string = nameParts[1]?.charAt(0).toUpperCase() ?? nameParts[0]?.charAt(1).toLowerCase() ?? '';

    return `${a}${b}`;
  });

  readonly avatarClass: Signal<string> = computed<string>(() => {
    return `custom-avatar-${this.avatarSize()}`;
  });

  readonly initialsClass: Signal<string> = computed<string>(() => {
    const size: AvatarSize = this.avatarSize();
    const baseClass: string = this.avatarClass();

    if (size === 'lg') return `${baseClass} or-head-30`;
    if (size === 'md') return `${baseClass} or-head-xl`;
    if (size === 'sm') return `${baseClass} or-head-md`;
    if (size === 'xs') return `${baseClass} or-head-xxs`;
    return '';
  });

  readonly avatarDimension: Signal<number> = computed<number>(() => {
    const size: AvatarSize = this.avatarSize();
    if (size === 'lg') return 100;
    if (size === 'md') return 60;
    if (size === 'sm') return 40;
    if (size === 'xs') return 20;
    return 40;
  });

  onImageError(): void {
    this.imageFallback.set(true);
  }
}
